// Raw async procedure functions. Each maps a former tRPC procedure to either
// a supabase-js DB query (for things RLS allows) or a `supabase.functions.invoke`
// call to one of our edge functions (for things that need server-side secrets).
//
// The `proceduresClient` shape mirrors the old tRPC client: `proceduresClient.<group>.<name>(input)`.
// This is wrapped by `trpcReact` and `trpcClient` proxies so existing call sites
// keep working with minimal changes.

// ---------- types kept compatible with the previous tRPC return shapes ----------

import type { BadgeType } from '@api/models/user/badge';
import type { Blockchain } from '@repo/prsn-blockchain';
import type { Address } from 'viem';

import { supabase } from './supabase';
import { env } from '@web/utils/env';

export interface ITwitterFront {
  displayName: string;
  handle: string;
  profilePictureURL: string;
  personaPFPTokenId: bigint | null | undefined;
}

export interface IPersonaFront {
  tokenId: bigint;
  blockchain: Blockchain;
  ownerAddress: Address;
  stakedAt: Date | null;
  rank: number | null;
  isCouncil?: boolean;
}

export interface IPublicUserFront {
  id: string;
  badges: BadgeType[];
  badgePoints: number;
  firstPersonaAcquiredAt?: Date;
  followersCount: number;
  followingsCount: number;
  twitter: ITwitterFront;
  bio: string;
  wallets: Address[];
  personas: IPersonaFront[];
}

export interface IUserFront extends Omit<IPublicUserFront, 'twitter'> {
  twitter?: ITwitterFront;
  boosts: number;
}

export interface IBadgePointsConfigEntry {
  code: string;
  points: number;
  earnersCount: number;
  totalUsers: number;
}

export interface IAuthCredentials {
  access: string;
  accessExpiresAt: Date;
  refresh: string;
}

export interface IPersonaListingFront {
  url: string;
  priceEth: number;
  currency: string;
  marketplace: string;
}

interface NonceResponse {
  nonce: string;
  signature: string;
  expiresAt: number;
}

// ---------- helpers ----------

async function invokeEdge<T>(
  name: string,
  body?: unknown,
  method: 'GET' | 'POST' = 'POST',
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, {
    body: body as Record<string, unknown> | undefined,
    method,
  });
  if (error) {
    // supabase-js wraps non-2xx responses; try to extract our error JSON.
    const message =
      (data as { error?: string } | null)?.error ??
      error.message ??
      'edge function failed';
    throw new Error(message);
  }
  return data as T;
}

interface DbUser {
  id: string;
  role: string;
  badges: string[];
  badges_outdated: boolean;
  badge_points: number;
  twitter_id: string | null;
  twitter_handle: string | null;
  twitter_display_name: string | null;
  twitter_profile_picture_url: string | null;
  twitter_total_followers: number | null;
  twitter_total_following: number | null;
  twitter_pfp_token_id: string | null;
  bio: string | null;
  first_persona_acquired_at: string | null;
  personas_count: number;
  followers_count: number;
  followings_count: number;
}

interface DbPersona {
  token_id: string | number;
  rank: number | null;
  is_council: boolean;
  owner_wallet: string | null;
  blockchain: string | null;
  staked_at: string | null;
}

function toBigInt(v: string | number | bigint | null | undefined): bigint {
  if (v == null) return 0n;
  if (typeof v === 'bigint') return v;
  return BigInt(v);
}

function userTwitterFront(u: DbUser): ITwitterFront | undefined {
  if (!u.twitter_id || !u.twitter_handle || !u.twitter_display_name) {
    return undefined;
  }
  return {
    displayName: u.twitter_display_name,
    handle: u.twitter_handle,
    profilePictureURL: u.twitter_profile_picture_url ?? '',
    personaPFPTokenId:
      u.twitter_pfp_token_id != null ? BigInt(u.twitter_pfp_token_id) : null,
  };
}

function personaToFront(p: DbPersona): IPersonaFront {
  if (!p.blockchain || !p.owner_wallet) {
    throw new Error('Cannot expose persona without location');
  }
  return {
    tokenId: toBigInt(p.token_id),
    blockchain: p.blockchain as Blockchain,
    ownerAddress: p.owner_wallet as Address,
    stakedAt: p.staked_at ? new Date(p.staked_at) : null,
    rank: p.rank,
    isCouncil: p.is_council,
  };
}

function userToFront(
  u: DbUser,
  wallets: Address[],
  personas: DbPersona[],
  boostsAvailable: number,
): IUserFront {
  return {
    id: u.id,
    badges: u.badges as BadgeType[],
    badgePoints: u.badge_points ?? 0,
    firstPersonaAcquiredAt: u.first_persona_acquired_at
      ? new Date(u.first_persona_acquired_at)
      : undefined,
    followersCount: u.followers_count,
    followingsCount: u.followings_count,
    twitter: userTwitterFront(u),
    bio: u.bio ?? '',
    wallets,
    personas: personas.map(personaToFront),
    boosts: boostsAvailable,
  };
}

function userToPublicFront(
  u: DbUser,
  wallets: Address[],
  personas: DbPersona[],
): IPublicUserFront {
  const twitter = userTwitterFront(u);
  if (!twitter) {
    throw new Error('Cannot expose user without twitter');
  }
  return {
    id: u.id,
    badges: u.badges as BadgeType[],
    badgePoints: u.badge_points ?? 0,
    firstPersonaAcquiredAt: u.first_persona_acquired_at
      ? new Date(u.first_persona_acquired_at)
      : undefined,
    followersCount: u.followers_count,
    followingsCount: u.followings_count,
    twitter,
    bio: u.bio ?? '',
    wallets,
    personas: personas.map(personaToFront),
  };
}

const USER_PUBLIC_COLS = `
  id, role, badges, badges_outdated, badge_points,
  twitter_id, twitter_handle, twitter_display_name, twitter_profile_picture_url,
  twitter_total_followers, twitter_total_following, twitter_pfp_token_id,
  bio, first_persona_acquired_at,
  personas_count, followers_count, followings_count
`;

async function loadWalletsForUsers(
  userIds: string[],
): Promise<Map<string, Address[]>> {
  const map = new Map<string, Address[]>();
  if (!userIds.length) return map;
  const { data, error } = await supabase
    .from('user_wallets')
    .select('user_id, address')
    .in('user_id', userIds);
  if (error) throw error;
  for (const row of data ?? []) {
    const arr = map.get(row.user_id as string) ?? [];
    arr.push(row.address as Address);
    map.set(row.user_id as string, arr);
  }
  return map;
}

async function loadPersonasForUsers(
  userIds: string[],
): Promise<Map<string, DbPersona[]>> {
  const map = new Map<string, DbPersona[]>();
  if (!userIds.length) return map;
  const { data, error } = await supabase
    .from('personas')
    .select(
      'owner_user_id, token_id, rank, is_council, owner_wallet, blockchain, staked_at',
    )
    .in('owner_user_id', userIds);
  if (error) throw error;
  for (const row of data ?? []) {
    const ownerId = row.owner_user_id as string | null;
    if (!ownerId) continue;
    const arr = map.get(ownerId) ?? [];
    arr.push(row);
    map.set(ownerId, arr);
  }
  return map;
}

// ---------- procedures ----------

const liveProcedures = {
  auth: {
    getNonce: async (): Promise<NonceResponse> => {
      return invokeEdge<NonceResponse>('auth-nonce', undefined, 'GET');
    },
    signInWithWallet: async (input: {
      message: string;
      signature: string;
      nonce: string;
      nonceSignature: string;
    }): Promise<IAuthCredentials> => {
      const r = await invokeEdge<{
        access: string;
        accessExpiresAt: string;
        refresh: string;
      }>('auth-siwe', input);
      return {
        access: r.access,
        accessExpiresAt: new Date(r.accessExpiresAt),
        refresh: r.refresh,
      };
    },
    refreshAuthCredentials: async (input: {
      access: string;
      refresh: string;
    }): Promise<IAuthCredentials> => {
      const r = await invokeEdge<{
        access: string;
        accessExpiresAt: string;
        refresh: string;
      }>('auth-refresh', { refresh: input.refresh });
      return {
        access: r.access,
        accessExpiresAt: new Date(r.accessExpiresAt),
        refresh: r.refresh,
      };
    },
    signOut: async (input: { refresh: string }): Promise<void> => {
      await invokeEdge('auth-signout', input);
    },
    // connectWallet / disconnectWallet are NOT yet wired as edge functions.
    // They were single-call mutations in the old API. For Phase C, we leave
    // them as no-ops that throw — the wallet-management UI on the settings
    // page can be re-enabled when those edge fns are added.
    connectWallet: async (_input: {
      message: string;
      signature: string;
    }): Promise<void> => {
      throw new Error(
        'connectWallet is not implemented in supabase-native phase yet',
      );
    },
    disconnectWallet: async (_address: string): Promise<void> => {
      throw new Error(
        'disconnectWallet is not implemented in supabase-native phase yet',
      );
    },
  },

  user: {
    me: async (): Promise<IUserFront> => {
      // Decode current user's id from the JWT we hold locally.
      const { getAuth } = await import('@web/utils/auth/manager');
      const auth = getAuth();
      if (!auth) throw new Error('Not authenticated');
      const userId = decodeJwtSub(auth.access);
      if (!userId) throw new Error('JWT missing sub');

      const [{ data: user, error: userErr }, walletsMap] = await Promise.all([
        supabase
          .from('users_public')
          .select(USER_PUBLIC_COLS)
          .eq('id', userId)
          .single(),
        loadWalletsForUsers([userId]),
      ]);
      if (userErr) throw userErr;
      // We need the FULL user row (incl. role) for /me, plus boosts.
      const wallets = walletsMap.get(userId) ?? [];
      const personasMap = await loadPersonasForUsers([userId]);
      const personas = personasMap.get(userId) ?? [];
      const { count: boostCount } = await supabase
        .from('user_boosts')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('claimed', false);
      return userToFront(user as DbUser, wallets, personas, boostCount ?? 0);
    },

    byTwitterHandle: async (handle: string): Promise<IPublicUserFront> => {
      const { data: user, error } = await supabase
        .from('users_public')
        .select(USER_PUBLIC_COLS)
        .eq('twitter_handle', handle)
        .maybeSingle();
      if (error) throw error;
      if (!user) throw new Error('User not found');
      const userId = (user as DbUser).id;
      const [walletsMap, personasMap] = await Promise.all([
        loadWalletsForUsers([userId]),
        loadPersonasForUsers([userId]),
      ]);
      return userToPublicFront(
        user as DbUser,
        walletsMap.get(userId) ?? [],
        personasMap.get(userId) ?? [],
      );
    },

    holders: async (input: {
      name?: string;
      notFollowed?: boolean;
      new?: boolean;
      cursor?: number;
      limit?: number;
      sort?: Record<string, 1 | -1>;
    }): Promise<{
      items: IPublicUserFront[];
      nextCursor: number | undefined;
    }> => {
      const limit = input.limit ?? 25;
      const cursor = input.cursor ?? 1;
      // Use the holders view (which already filters to personas_count>0 + twitter linked).
      let q = supabase.from('holders').select('*');
      if (input.name) {
        // Holders view exposes display_name + twitter_handle; filter by ilike.
        q = q.or(
          `display_name.ilike.%${input.name}%,twitter_handle.ilike.%${input.name}%`,
        );
      }
      // sort handling: only personas_count is in the holders view; ignore others
      // for now (the program/holders page uses this to sort by # of personas).
      const ascByPersonas = input.sort?.['search.personasCount'] === 1;
      q = q.order('personas_count', { ascending: ascByPersonas });
      q = q.range((cursor - 1) * limit, cursor * limit - 1);

      const { data, error } = await q;
      if (error) throw error;
      // The view emits a JSON aggregate with camelCase keys (tokenId,
      // stakedAt, pHash, blockchain, rank) and intentionally omits owner_wallet
      // / is_council to keep the payload small. The program holder list only
      // reads `holder.personas.length`, so we don't run personaToFront here —
      // doing so would throw because owner_wallet is absent.
      interface ViewPersona {
        tokenId: number | string;
        rank: number | null;
        blockchain: Blockchain;
        stakedAt: string | null;
      }
      const items = (data ?? []).map((row: any) => {
        const viewPersonas = (row.personas ?? []) as ViewPersona[];
        const personas: IPersonaFront[] = viewPersonas.map((p) => ({
          tokenId: toBigInt(p.tokenId),
          blockchain: p.blockchain,
          ownerAddress: '0x' as Address,
          stakedAt: p.stakedAt ? new Date(p.stakedAt) : null,
          rank: p.rank,
          isCouncil: false,
        }));
        return {
          id: row.user_id as string,
          badges: [] as BadgeType[],
          badgePoints: 0,
          followersCount: 0,
          followingsCount: 0,
          twitter: {
            displayName: row.display_name ?? '',
            handle: row.twitter_handle ?? '',
            profilePictureURL: '',
            personaPFPTokenId: row.persona_pfp_token_id
              ? BigInt(row.persona_pfp_token_id)
              : null,
          },
          bio: '',
          wallets: [] as Address[],
          personas,
        } satisfies IPublicUserFront;
      });
      return {
        items,
        nextCursor: items.length >= limit ? cursor + 1 : undefined,
      };
    },

    holdersCount: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('users_public')
        .select('id', { count: 'exact', head: true })
        .gt('personas_count', 0)
        .not('twitter_id', 'is', null);
      if (error) throw error;
      return Math.max(0, (count ?? 0) - 1);
    },

    badgeRarity: async (): Promise<Record<string, number>> => {
      // Aggregation isn't trivial via supabase-js; we fall back to a SQL RPC
      // call. For now return empty until we deploy a SQL function for it.
      return {};
    },

    badgePointsConfig: async (): Promise<IBadgePointsConfigEntry[]> => {
      const { data, error } = await supabase
        .from('badge_points_config')
        .select('code, points, earners_count, total_users');
      if (error) throw error;
      return ((data ?? []) as Array<{
        code: string;
        points: number;
        earners_count: number;
        total_users: number;
      }>).map((r) => ({
        code: r.code,
        points: r.points,
        earnersCount: r.earners_count,
        totalUsers: r.total_users,
      }));
    },

    badgePointsRank: async (
      input: { userId: string },
    ): Promise<{ rank: number; total: number; points: number }> => {
      // Pull the user's points first so we know what to count against.
      const { data: meRow, error: meErr } = await supabase
        .from('users_public')
        .select('badge_points')
        .eq('id', input.userId)
        .maybeSingle();
      if (meErr) throw meErr;
      const myPoints = (meRow?.badge_points as number | null) ?? 0;
      const { count: aheadCount, error: aheadErr } = await supabase
        .from('users_public')
        .select('id', { count: 'exact', head: true })
        .gt('badge_points', myPoints);
      if (aheadErr) throw aheadErr;
      const { count: totalCount, error: totalErr } = await supabase
        .from('users_public')
        .select('id', { count: 'exact', head: true });
      if (totalErr) throw totalErr;
      return {
        rank: (aheadCount ?? 0) + 1,
        total: totalCount ?? 0,
        points: myPoints,
      };
    },

    updateBio: async (input: { bio: string }): Promise<void> => {
      const { getAuth } = await import('@web/utils/auth/manager');
      const auth = getAuth();
      if (!auth) throw new Error('Not authenticated');
      const userId = decodeJwtSub(auth.access);
      if (!userId) throw new Error('JWT missing sub');
      const { error } = await supabase
        .from('users')
        .update({ bio: input.bio })
        .eq('id', userId);
      if (error) throw error;
    },

    generateBoostSignature: async (input: {
      tokenIds: bigint[];
      owner: Address;
    }): Promise<{
      bonusesPerDay: bigint[];
      signatures: { expiration: number; nonce: bigint; value: `0x${string}` }[];
    }> => {
      const r = await invokeEdge<{
        bonusesPerDay: string[];
        signatures: {
          expiration: number;
          nonce: string;
          value: `0x${string}`;
        }[];
      }>('boost-signature', {
        tokenIds: input.tokenIds.map((t) => t.toString()),
        owner: input.owner,
      });
      return {
        bonusesPerDay: r.bonusesPerDay.map((s) => BigInt(s)),
        signatures: r.signatures.map((s) => ({
          expiration: s.expiration,
          nonce: BigInt(s.nonce),
          value: s.value,
        })),
      };
    },

    afterBoostTransaction: async (input: {
      tokenIds: bigint[];
      success: boolean;
    }): Promise<void> => {
      await invokeEdge('boost-after-tx', {
        tokenIds: input.tokenIds.map((t) => t.toString()),
        success: input.success,
      });
    },

    twitter: {
      connectTwitter: async (input: {
        code: string;
        challenge: string;
        redirectURI: string;
      }): Promise<void> => {
        await invokeEdge('twitter-oauth', input);
      },
      disconnectTwitter: async (): Promise<void> => {
        await invokeEdge('twitter-disconnect');
      },
      followings: async (): Promise<string[]> => {
        const { getAuth } = await import('@web/utils/auth/manager');
        const auth = getAuth();
        if (!auth) return [];
        const userId = decodeJwtSub(auth.access);
        if (!userId) return [];
        const { data, error } = await supabase
          .from('follows')
          .select('following')
          .eq('follower', userId);
        if (error) throw error;
        return (data ?? []).map((r) => r.following as string);
      },
    },
  },

  persona: {
    getOwner: async (
      tokenId: number,
    ): Promise<{
      blockchain: Blockchain | null;
      address: Address | null;
      user: IPublicUserFront | null;
    } | null> => {
      const { data: persona, error } = await supabase
        .from('personas')
        .select('owner_wallet, owner_user_id, blockchain')
        .eq('token_id', tokenId)
        .maybeSingle();
      if (error) throw error;
      if (!persona) throw new Error('persona not found');
      // 13 anomalous personas have null owner_wallet/blockchain/rank in the
      // migrated dataset (kept nullable in the schema). Return the row with
      // null fields rather than null-the-whole-thing so the UI can render an
      // "owner unknown" state instead of a perpetual loading skeleton.
      if (!persona.owner_wallet) {
        return {
          blockchain: persona.blockchain as Blockchain | null,
          address: null,
          user: null,
        };
      }
      let user: IPublicUserFront | null = null;
      if (persona.owner_user_id) {
        const { data: u } = await supabase
          .from('users_public')
          .select(USER_PUBLIC_COLS)
          .eq('id', persona.owner_user_id)
          .not('twitter_id', 'is', null)
          .maybeSingle();
        if (u) {
          const walletsMap = await loadWalletsForUsers([u.id as string]);
          try {
            user = userToPublicFront(
              u as DbUser,
              walletsMap.get(u.id as string) ?? [],
              [],
            );
          } catch {
            user = null;
          }
        }
      }
      return {
        blockchain: persona.blockchain as Blockchain | null,
        address: persona.owner_wallet as Address,
        user,
      };
    },

    getListing: async (
      tokenId: number,
    ): Promise<IPersonaListingFront | null> => {
      const { data, error } = await supabase
        .from('personas')
        .select(
          'listing_price_eth, listing_currency, listing_url, listing_marketplace',
        )
        .eq('token_id', tokenId)
        .maybeSingle();
      if (error) throw error;
      if (!data?.listing_price_eth || !data.listing_url) return null;
      return {
        url: data.listing_url as string,
        priceEth: Number(data.listing_price_eth),
        currency: (data.listing_currency as string | null) ?? 'ETH',
        marketplace: (data.listing_marketplace as string | null) ?? 'opensea',
      };
    },

    getActiveListings: async (): Promise<Record<number, IPersonaListingFront>> => {
      const { data, error } = await supabase
        .from('personas')
        .select(
          'token_id, listing_price_eth, listing_currency, listing_url, listing_marketplace',
        )
        .not('listing_price_eth', 'is', null);
      if (error) throw error;
      const out: Record<number, IPersonaListingFront> = {};
      for (const row of data ?? []) {
        const r = row as {
          token_id: number;
          listing_price_eth: string | number | null;
          listing_currency: string | null;
          listing_url: string | null;
          listing_marketplace: string | null;
        };
        if (!r.listing_price_eth || !r.listing_url) continue;
        out[Number(r.token_id)] = {
          url: r.listing_url,
          priceEth: Number(r.listing_price_eth),
          currency: r.listing_currency ?? 'ETH',
          marketplace: r.listing_marketplace ?? 'opensea',
        };
      }
      return out;
    },

    getCollectionStats: async (): Promise<{
      personas: Record<Blockchain | 'staked', number>;
      holders: Record<'withPFP' | 'withoutPFP', number>;
    }> => {
      const [byChain, staked, holdersData] = await Promise.all([
        supabase
          .from('personas')
          .select('blockchain', { count: 'exact', head: false })
          .not('blockchain', 'is', null),
        supabase
          .from('personas')
          .select('id', { count: 'exact', head: true })
          .not('staked_at', 'is', null),
        supabase
          .from('users_public')
          .select('twitter_pfp_token_id', { count: 'exact', head: false })
          .gt('personas_count', 0)
          .not('twitter_id', 'is', null),
      ]);
      // Tally blockchain counts client-side.
      const counts: Record<string, number> = {};
      for (const row of byChain.data ?? []) {
        const k = (row as { blockchain: string }).blockchain;
        counts[k] = (counts[k] ?? 0) + 1;
      }
      let withPFP = 0;
      let withoutPFP = 0;
      for (const row of holdersData.data ?? []) {
        if (
          (row as { twitter_pfp_token_id: string | null })
            .twitter_pfp_token_id != null
        ) {
          withPFP++;
        } else {
          withoutPFP++;
        }
      }
      return {
        personas: {
          ethereum: counts.ethereum ?? 0,
          base: counts.base ?? 0,
          staked: staked.count ?? 0,
        } as Record<Blockchain | 'staked', number>,
        holders: { withPFP, withoutPFP },
      };
    },
  },
};

// ---------- public build ----------

/**
 * The public build ships without an account layer: there is no sign-in, so
 * nothing that needs one is ever reached. The three reads the collection grid
 * and the Council room still make resolve locally instead of calling out — so
 * no price chips and no owner names, which is exactly what those surfaces
 * already render for a token that has neither.
 *
 * Everything else keeps its live implementation, so clearing the flag restores
 * the app unchanged.
 */
const shellProcedures: typeof liveProcedures = {
  ...liveProcedures,
  persona: {
    ...liveProcedures.persona,
    getOwner: async () => null,
    getListing: async () => null,
    getActiveListings: async () => ({}),
  },
};

export const proceduresClient = env.SHELL_MODE
  ? shellProcedures
  : liveProcedures;

// ---------- helpers ----------

function decodeJwtSub(token: string): string | null {
  try {
    const [, payloadB64] = token.split('.');
    if (!payloadB64) return null;
    const padded = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
    const payload = JSON.parse(json) as { sub?: string };
    return payload.sub ?? null;
  } catch {
    return null;
  }
}

export type ProceduresClient = typeof proceduresClient;
